import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import get from 'lodash/get';
import { useTranslation } from '../../src/i18n';
import { colors, boxShadows, fontSize } from '../../constants/styleConstants';
import { AgreementsDataV1 } from '../../containers/Managers/ArtistRoster/ArtistRosterElements';
import NextLink from '../NextLink';
import { route } from '../../constants/routes';
import { TP, ARTIST_TAB_VALUES } from '../../constants/consts';
import ContactLinks from '../ContactLinks';
import email from '../../public/images/icons/email.png';
import phone from '../../public/images/icons/phone.png';
import ImageTag from '../ImageTag';

const useStyles = makeStyles(theme => ({
  agencyCard: {
    borderRadius: '8px',
    boxShadow: boxShadows.sideCard,
    border: `solid 1px ${colors.border.grey}`,
    marginBottom: 20,
  },
  rightArrow: {
    fontSize: '1.1rem',
    color: colors.blueChip,
  },
  cardTitle: {
    display: 'flex',
    marginBottom: 16,
  },
  titleLinkContainer: {
    margin: 'auto',
    marginRight: 0,
    cursor: 'pointer',
  },
  titleTextContainer: {
    fontFamily: 'Roboto',
    fontSize: 16,
    fontWeight: 'bold',
    fontStyle: 'normal',
    letterSpacing: 0,
    color: colors.grey3,
  },
  title: {
    fontSize: fontSize.medium,
    margin: 0,
    color: colors.grey3,
  },
  agencyLink: {
    fontSize: fontSize.basic,
    fontWeight: 'bold',
    color: colors.blueChip,
  },
  agencyContact: {
    paddingTop: 12,
    '& > div': {
      paddingBottom: props => (!props.isSmall ? 16 : 'inherit'),
    },
  },
  footer: {
    borderTop: `1px solid ${colors.contentDivider}`,
    padding: 15,
    display: 'flex',
    justifyContent: 'center',
    fontSize: 15,
    fontWeight: 'bold',
    color: colors.blueChip,
    '& span': {
      cursor: 'pointer',
    },
  },
  agencyBody: {
    padding: 15,
  },
  representationDetails: {
    marginTop: 10,
    '& p': {
      paddingBottom: 8,
    },
  },
  emptyAgency: {
    fontSize: fontSize.smallTitle,
    fontStyle: 'italic',
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  agentDetails: {},
  agentProfile: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    '& img': {
      width: 35,
      height: 35,
      marginRight: 13,
    },
    '& span': {
      fontSize: 15,
      color: colors.grey3,
      fontWeight: 'bold',
    },
  },
  repInfo: {
    display: 'flex',
    alignContent: 'center',
    marginTop: 10,
    '& img': {
      width: 25,
      height: 25,
    },
  },
}));

const useAgreementStyles = makeStyles(theme => ({
  agreementsInfo: {
    textTransform: 'uppercase',
    display: 'flex',
    marginBottom: 12,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  label: {
    fontSize: fontSize.smallTitle,
    color: colors.subTitle,
    fontWeight: 'bold',
  },
  reagionItem: {
    backgroundColor: colors.grey,
    padding: 5,
    fontSize: 9,
    fontWeight: 400,
    marginRight: 5,
    textTransform: 'uppercase',
    display: 'flex-row',
    wordBreak: 'break-word',
  },
  innerCell: {
    borderLeft: `1px solid ${colors.linesGray}`,
  },
  avatar: {
    marginRight: 10,
  },
  artistNameBlock: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  editButtonsBlock: {
    marginLeft: 'auto',
    maxWidth: '80px',
    padding: 10,
  },
  editLink: {
    marginRight: 10,
  },
  bold: {
    fontWeight: 'bold',
  },
}));

const ArtistAgency = ({ agreementData, isSmall, goToTab }) => {
  const classes = useStyles({ isSmall });
  const commonClasses = useAgreementStyles();
  const { t } = useTranslation();
  const agreeData = get(agreementData, 'data', []);
  const agencies = get(agreeData, 'agencies', []);

  const filteredAgencies = agencies.filter(item => item.agency);

  const agreementsRaw = get(agreeData, 'agreements', []);
  const agreements = agreementsRaw.map(item => ({
    ...item,
    includedCountries: [
      ...item.regions.filter(c => c.isExcluded === false),
      ...item.countries.filter(c => c.isExcluded === false),
    ],
    excludedCountries: [
      ...item.regions.filter(c => c.isExcluded === true),
      ...item.countries.filter(c => c.isExcluded === true),
    ],
  }));

  return (
    <>
      <div className={classes.cardTitle}>
        <div className={classes.titleTextContainer}>{t(`${TP}.m_ARTIST_CONTACT`)}</div>
        {isSmall && (
          <div className={classes.titleLinkContainer}>
            <a onClick={() => goToTab(ARTIST_TAB_VALUES.CONTACT)}>
              <ArrowForwardIcon className={classes.rightArrow} />
            </a>
          </div>
        )}
      </div>
      {filteredAgencies.map((agency, index) => {
        const agencyAgreements = agreements.filter(a => a.connection.id === agency.id);
        return (
          <div className={classes.agencyCard} key={`agency-${index}`}>
            <div className={classes.agencyBody}>
              <p className={classes.title}>{t(`${TP}.M_AGENCY`)} </p>
              <NextLink
                className={classes.agencyLink}
                as={`${route.MANAGERS}/${get(agency, 'agency.slug')}-${get(agency, 'agency.id')}`}
                href={`${route.MANAGERS}/[id]`}
              >
                {get(agency, 'agency.name')}
              </NextLink>
              {agencyAgreements.length > 0 && !isSmall && (
                <div className={classes.representationDetails}>
                  <p className={classes.title}>{t(`${TP}.m_REPRESENTATIONDETAIL`)}</p>

                  <AgreementsDataV1
                    agreements={agencyAgreements}
                    t={t}
                    commonClasses={commonClasses}
                    isMinimized={isSmall}
                  />
                </div>
              )}
              {agency?.agency?.contacts.length > 0 && (
                <div className={classes.agencyContact}>
                  {!isSmall && <div className={classes.title}> {t(`${TP}.AGENCY_CONTACT`)} </div>}
                  <ContactLinks contacts={agency?.agency?.contacts} />
                </div>
              )}

              {!isSmall && get(agencyAgreements, '[0].contactPersons', []).length > 0 && (
                <div className={classes.agentDetails}>
                  <p className={classes.title}>{t(`${TP}.AGENTS_REPRESENTATIVES`)} </p>
                  {agencyAgreements[0].contactPersons.map(cp => (
                    <div className={classes.agentProfile}>
                      {cp?.profile?.image && <ImageTag src={cp?.profile?.image} alt="profile" />}
                      <span>{cp?.agentName}</span>
                      {cp.agentPhoneNumber && (
                        <div className={classes.repInfo}>
                          <ImageTag src={phone} alt="phone" />
                          {cp.agentPhoneNumber}
                        </div>
                      )}
                      {cp.agentEmail && (
                        <div className={classes.repInfo}>
                          <ImageTag src={email} alt="email" />
                          <a href={`mailto:${cp.agentEmail}`}>{cp.agentEmail}</a>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* <ContactLinks contacts={agency?.agent?.profile?.contacts} /> */}
                </div>
              )}
            </div>

            {isSmall && (
              <div className={classes.footer}>
                <span onClick={() => goToTab(ARTIST_TAB_VALUES.CONTACT)}>
                  {' '}
                  {t(`${TP}.VIEW_REPRESENTATION_DETAILS`)}{' '}
                </span>
              </div>
            )}
          </div>
        );
      })}
      {!isSmall && filteredAgencies.length <= 0 && (
        <div className={classes.emptyAgency}>{t(`${TP}.NO_ARTIST_AGENCY_DATA`)} </div>
      )}
    </>
  );
};

ArtistAgency.propTypes = {
  agreementData: PropTypes.object,
  isSmall: PropTypes.bool,
  goToTab: PropTypes.func,
};

export default ArtistAgency;
